import React from 'react';

import {
  BadgePlus,
  ChartPie,
  ChevronRight,
  CreditCard,
  Info,
  ReceiptText,
  RefreshCw,
} from 'lucide-react';

import imgStarIcon from '@/assets/icons/star.png';
import Modal from '@/components/common/Modal';
import type { PointsSummary } from '@/types';

import { formatPointsCount } from '../utils/pointsBalanceDisplay';

interface PointsCenterModalProps {
  open: boolean;
  summary: PointsSummary | null;
  loading: boolean;
  error: string | null;
  userLabel: string;
  onClose: () => void;
  onRetry: () => void;
  onOpenHistory: () => void;
  onOpenRechargeRecords?: () => void;
  canViewGlobalBilling?: boolean;
  onOpenGlobalBilling?: () => void;
  onOpenAdminRecharge?: () => void;
  onOpenBuy?: () => void;
}

const summaryItems = (summary: PointsSummary | null) => [
  {
    label: '累计获得',
    value: summary ? formatPointsCount(summary.total_points) : '--',
  },
  {
    label: '已用积分',
    value: summary ? formatPointsCount(summary.used_points) : '--',
  },
];

const PointsCenterModal: React.FC<PointsCenterModalProps> = ({
  open,
  summary,
  loading,
  error,
  userLabel,
  onClose,
  onRetry,
  onOpenHistory,
  onOpenRechargeRecords,
  canViewGlobalBilling = false,
  onOpenGlobalBilling,
  onOpenAdminRecharge,
  onOpenBuy,
}) => (
  <Modal
    title="积分中心"
    description={`当前用户 · ${userLabel}`}
    open={open}
    onClose={onClose}
    width="min(680px, calc(100vw - 48px))"
  >
    {loading && !summary ? (
      <div className="space-y-4">
        <div className="h-44 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="h-16 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
          <div className="h-16 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
        </div>
      </div>
    ) : error ? (
      <div className="flex h-56 flex-col items-center justify-center gap-3 rounded-lg border border-dark-border bg-dark-card">
        <p className="text-sm font-medium text-dark-text">积分信息加载失败</p>
        <p className="max-w-sm text-center text-sm text-dark-text-secondary">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-theme-color px-4 text-sm text-theme-color transition-colors hover:bg-theme-color-10"
        >
          <RefreshCw className="h-4 w-4" />
          重试
        </button>
      </div>
    ) : (
      <div className="space-y-4">
        <section
          className="relative rounded-[16px] p-5 overflow-visible"
          style={{
            background: 'linear-gradient(to bottom left, #3ACB99 0%, #28BD8B 100%)',
            boxShadow: '0 8px 24px 0 rgba(35, 179, 131, 0.35)',
          }}
        >
          <img
            src={imgStarIcon}
            alt="积分"
            className="absolute -top-5 right-4 h-[140px] w-[140px] pointer-events-none"
          />
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <div
                  data-testid="points-current-label"
                  className="text-sm font-medium text-white/90"
                >
                  当前积分
                </div>
                {onOpenBuy ? (
                  <button
                    type="button"
                    onClick={onOpenBuy}
                    className="rounded-full bg-white px-3 py-0.5 text-[12px] font-medium transition-colors hover:bg-white/90"
                    style={{ color: '#23b383' }}
                  >
                    积分购买
                  </button>
                ) : null}
              </div>
              <p
                data-testid="points-current-value"
                className="mt-4 text-5xl font-semibold leading-none text-white tabular-nums"
                style={{ fontFamily: 'AlibabaSans102-Bd' }}
              >
                {summary ? formatPointsCount(summary.available_points) : '--'}
              </p>
            </div>
          </div>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {summaryItems(summary).map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-white/20 bg-white/20 px-3 py-2"
              >
                <p className="text-xs text-white/90">{item.label}</p>
                <p
                  className="mt-1 text-lg font-semibold text-white tabular-nums"
                  style={{ fontFamily: 'AlibabaSans102-Bd' }}
                >
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </section>

        <div className="grid gap-3 sm:grid-cols-2 [&>:last-child:nth-child(odd)]:col-span-full">
          <button
            type="button"
            onClick={onOpenHistory}
            className="flex min-h-[72px] items-center justify-between rounded-lg border border-dark-border bg-dark-card px-4 text-left transition-colors hover:border-theme-color hover:bg-theme-color-10"
          >
            <span className="flex min-w-0 items-center gap-3">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-theme-color-10 text-theme-color">
                <ReceiptText className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-sm font-medium text-dark-text">积分明细</span>
                <span className="mt-1 block text-xs text-dark-text-secondary">
                  查看流水、模型和任务记录
                </span>
              </span>
            </span>
            <ChevronRight className="h-5 w-5 shrink-0 text-dark-text-secondary" />
          </button>
          {onOpenRechargeRecords ? (
            <button
              type="button"
              data-testid="points-center-payment-orders"
              onClick={onOpenRechargeRecords}
              className="flex min-h-[72px] items-center justify-between rounded-lg border border-dark-border bg-dark-card px-4 text-left transition-colors hover:border-theme-color hover:bg-theme-color-10"
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-theme-color-10 text-theme-color">
                  <CreditCard className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-dark-text">充值记录</span>
                  <span className="mt-1 block text-xs text-dark-text-secondary">
                    查看成功充值订单
                  </span>
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-dark-text-secondary" />
            </button>
          ) : null}
          {canViewGlobalBilling && onOpenGlobalBilling ? (
            <button
              type="button"
              data-testid="points-center-global-billing"
              onClick={onOpenGlobalBilling}
              className="flex min-h-[72px] items-center justify-between rounded-lg border border-dark-border bg-dark-card px-4 text-left transition-colors hover:border-theme-color hover:bg-theme-color-10"
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-theme-color-10 text-theme-color">
                  <ChartPie className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-dark-text">全局积分</span>
                  <span className="mt-1 block text-xs text-dark-text-secondary">
                    查看整体积分消耗
                  </span>
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-dark-text-secondary" />
            </button>
          ) : null}
          {canViewGlobalBilling && onOpenAdminRecharge ? (
            <button
              type="button"
              data-testid="points-center-admin-recharge"
              onClick={onOpenAdminRecharge}
              className="flex min-h-[72px] items-center justify-between rounded-lg border border-dark-border bg-dark-card px-4 text-left transition-colors hover:border-theme-color hover:bg-theme-color-10"
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-theme-color-10 text-theme-color">
                  <BadgePlus className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-dark-text">积分赠送</span>
                  <span className="mt-1 block text-xs text-dark-text-secondary">
                    给任意用户赠送积分
                  </span>
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-dark-text-secondary" />
            </button>
          ) : null}
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-theme-color/20 bg-theme-color-10 px-4 py-3 text-sm text-dark-text">
          <Info className="h-4 w-4 shrink-0 text-theme-color" />
          积分用于 AI 生成与模型调用，按实际消耗扣除，失败自动返还，明细可查
        </div>
      </div>
    )}
  </Modal>
);

export default PointsCenterModal;
